/** @jsx jsx **/
import { Box, jsx, Text, Button, SxStyleProp } from 'theme-ui'
import { FC, useCallback, useState, useEffect } from 'react'
import { FilterCategory as FilterCategoryType } from '~/@types/models'
import { Checkbox } from '~/components/checkbox'
import Chevron from '~/components/icons/chevron'
import { ToggleSwitch } from './toogle'
import { TChangeItem } from '~/context/filters'

type Props = {
  category: FilterCategoryType
  selectedValues?: Array<{
    name: string
    value: string
  }>
  titleSx?: SxStyleProp
  moreFiltersSx?: SxStyleProp
  handleChange: (value: TChangeItem, categoryName: string) => void
  showMoreFilters: boolean
}

const FilterCategory: FC<Props> = ({
  handleChange,
  category,
  selectedValues = [],
  moreFiltersSx,
}) => {
  const [showOptions, setShowOptions] = useState<boolean>(!!selectedValues?.length)
  const [direction, setDirection] = useState<boolean>(false)
  const handleShowCategory = () => {
    setShowOptions(!showOptions)
    setDirection(!direction)
  }

  const isSelected = useCallback(
    (value: string) => {
      if (!selectedValues.length) return false
      return !!selectedValues.filter(selectedValue => selectedValue.value === value).length
    },
    [selectedValues]
  )

  useEffect(() => {
    if (selectedValues.length) {
      setDirection(true)
      setShowOptions(true)
    }
  }, [selectedValues])

  // useEffect(() => {
  //   setShowOptions(showMoreFilters)
  //   setDirection(showMoreFilters)
  // }, [showMoreFilters])

  if (category?.type === 'boolean') {
    const handleBooleanChange = (value: boolean) => {
      if (value) {
        handleChange({ name: category.items[0].name, value }, category.name)
      } else {
        handleChange(null, category.name)
      }
    }

    return (
      <ToggleSwitch
        label={category.label}
        value={isSelected(category?.items[0]?.value)}
        onChange={handleBooleanChange}
      />
    )
  }

  if (!category?.items?.length) return null

  return (
    <Box
      sx={{
        width: '100%',
        borderBottom: ['1px solid', null, null, 'unset'],
        borderColor: ['border', null, null, 'unset'],
        display: 'flex',
        flexDirection: 'column',
        p: [8, null, null, 0],
        mb: 0,
        ...moreFiltersSx,
      }}
    >
      <Button
        sx={{
          backgroundColor: 'white',
          color: 'black',
          pt: '4px',
          borderRadius: 0,
          marginBottom: showOptions ? '16px' : 0,
          paddingBottom: showOptions ? '16px' : 0,
          borderBottom: [showOptions ? '1px solid' : 'none', null, null, 'unset'],
          borderColor: ['border', null, null, 'unset'],
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
          onClick={handleShowCategory}
        >
          <Text
            as="button"
            variant="label"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              color: '#041826',
              backgroundColor: 'transparent',
              fontWeight: 400,
              fontSize: '13px !important',
              lineHeight: '19.9px',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              border: 'none',
              p: 0,
            }}
          >
            {category.label}
          </Text>
          <Chevron direction={direction ? 'up' : 'down'} color="#041826" />
        </Box>
      </Button>
      <Box
        sx={{
          pb: [showOptions ? 10 : 0, null, null, 0],
          overflow: 'scroll',
          minWidth: '175px',
          overflowX: 'hidden',
          overflowY: 'hidden',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          height: showOptions ? 'auto' : 0,
          transition: 'opacity 0.3s ease',
          opacity: showOptions ? 1 : 0,
          '.checkbox': {
            width: '14px',
            height: '14px',
            minWidth: '14px',
            minHeight: '14px',
            position: 'relative',
            marginRight: '8px',
            '&:before': {
              width: '10px',
              height: '10px',
              left: '1px',
              top: '1px',
              transform: 'none',
            },
          },
        }}
        style={{ '&::WebkitScrollbar': { width: 0, height: 0 } }}
      >
        {category.items.map(value => {
          if (!value.name || !value.value) return null
          return (
            <Checkbox
              key={value.name}
              onChange={() => handleChange(value, category.name)}
              label={value.name}
              checked={isSelected(value.value)}
              name={`${category.label}[${value.name}]`}
              value={isSelected(value.value)}
              labelSx={{
                color: '#808080',
                fontWeight: isSelected(value.value) ? 'bold' : 400,
                fontSize: '13px',
                lineHeight: '15px',
                letterSpacing: '0.5px',
                textTransform: 'capitalize',
              }}
            />
          )
        })}
      </Box>
    </Box>
  )
}

export default FilterCategory
